import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { API_BASE_URL } from '../constants/api';

// Public, cached site-config reads — issue categories (Support page + ticket
// form) and the singleton Contact Info (shared by Support page, Footer,
// Navbar, Booking confirm).
export const siteContentApi = createApi({
  reducerPath: 'siteContentApi',
  baseQuery: fetchBaseQuery({ baseUrl: API_BASE_URL }),
  keepUnusedDataFor: 300, // 5 min cache — these rarely change during a session
  endpoints: (builder) => ({
    listIssueCategories: builder.query({
      query: () => '/issue-categories',
      // Both levels. `categories` is top-level only (the Support page draws
      // it as a card grid, so folding sub-categories in would turn 8 cards
      // into 40); `subcategories` carries a `parent_key` the form filters on
      // once a category is chosen.
      transformResponse: (raw) => ({
        categories: raw?.categories ?? [],
        subcategories: raw?.subcategories ?? [],
      }),
    }),
    getContactInfo: builder.query({
      query: () => '/contact-info',
      transformResponse: (raw) => raw?.contact ?? null,
    }),
    listFaqs: builder.query({
      query: () => '/faqs',
      transformResponse: (raw) => raw?.faqs ?? [],
    }),

    // ─── Blog ─────────────────────────────────────────────
    listBlogCategories: builder.query({
      query: () => '/blog/categories',
      transformResponse: (raw) => raw?.categories ?? [],
    }),
    listBlogArticles: builder.query({
      query: (categorySlug) => {
        const qs = categorySlug ? `?category=${encodeURIComponent(categorySlug)}` : '';
        return `/blog/articles${qs}`;
      },
      transformResponse: (raw) => raw?.articles ?? [],
    }),
    getBlogArticleBySlug: builder.query({
      query: (slug) => `/blog/articles/${encodeURIComponent(slug)}`,
      transformResponse: (raw) => raw?.article ?? null,
    }),

    // ─── Membership ──────────────────────────────────────
    getMembershipSettings: builder.query({
      query: () => '/membership/settings',
      transformResponse: (raw) => raw?.settings ?? null,
    }),
    listMembershipPlans: builder.query({
      query: () => '/membership/plans',
      transformResponse: (raw) => raw?.plans ?? [],
    }),

    // ─── About page ──────────────────────────────────────
    listAboutTimeline: builder.query({
      query: () => '/about/timeline',
      transformResponse: (raw) => raw?.timeline ?? [],
    }),
    listAboutNumbers: builder.query({
      query: () => '/about/numbers',
      transformResponse: (raw) => raw?.numbers ?? [],
    }),
    listAboutTeam: builder.query({
      query: () => '/about/team',
      transformResponse: (raw) => raw?.team ?? [],
    }),
    listAboutAdvisory: builder.query({
      query: () => '/about/advisory',
      transformResponse: (raw) => raw?.advisory ?? [],
    }),

    // ─── Doctors (public /doctors page) ─────────────────────
    // Admin-curated at /admin/settings/doctors. Returns { doctors: [...] }.
    listDoctors: builder.query({
      query: () => '/doctors',
      transformResponse: (raw) => raw?.doctors ?? [],
    }),

    // ─── Partnership form (write-only, no user-side tracking) ───
    submitPartnershipInquiry: builder.mutation({
      query: (body) => ({
        url: '/partnerships',
        method: 'POST',
        body,
      }),
    }),

    // ─── Trustian application (write-only) ───
    // POSTed from the "Become a Trustian today" dialog on /about. Backend
    // notifies the People team via FCM and the application surfaces in the
    // admin panel for triage.
    submitTrustianApplication: builder.mutation({
      query: (body) => ({
        url: '/trustian-applications',
        method: 'POST',
        body,
      }),
    }),

    // ─── Specials / Offers (admin-curated) ─────────────────
    // Only returns active + in-validity-window offers; backend handles
    // the date filter so the client just renders what it gets.
    listOffers: builder.query({
      query: () => '/offers',
      transformResponse: (raw) => raw?.offers ?? [],
    }),

    // ─── Recent tests (unique, by last-booked) ─────────────
    // Drives the home-page "Popular Tests" strip. Backend aggregates over
    // bookings and dedupes on test_id, so no duplicates ever land here.
    listRecentTests: builder.query({
      query: (limit = 8) => `/tests/recent?limit=${limit}`,
      transformResponse: (raw) => raw?.tests ?? [],
    }),

    // ─── Popular tests (admin-curated) ─────────────────────
    // Drives the home-page "Popular Tests" strip when admins want explicit
    // control over what's featured. Returns rows already shaped for TestCard.
    listPopularTests: builder.query({
      query: () => '/popular-tests',
      transformResponse: (raw) => raw?.tests ?? [],
    }),

    // ─── Popular packages (admin-curated) ─────────────────
    // Drives the home-page "Our most popular packages" strip when admins
    // want explicit control. Returns rows shaped for PackageCard.
    listPopularPackages: builder.query({
      query: () => '/popular-packages',
      transformResponse: (raw) => raw?.packages ?? [],
    }),

    // ─── Cities (home-page "Where We Operate") ────────────
    // Only returns active cities in display order; admins control the
    // list via /settings/home/cities in the admin panel.
    listCities: builder.query({
      query: () => '/cities',
      transformResponse: (raw) => raw?.cities ?? [],
    }),

    // ─── Centres (public /centres page) ───────────────────
    // Walk-in lab centre list; admin-curated via /admin/centres.
    listCentres: builder.query({
      query: () => '/centres',
      transformResponse: (raw) => raw?.centres ?? [],
    }),

    // ─── App settings (home page "Download the TrustLab App") ─────
    // Singleton — store links + rating / downloads / tests_available
    // stat cards; admin-curated via /settings/app in the admin panel.
    getAppSettings: builder.query({
      query: () => '/app-settings',
      transformResponse: (raw) => raw?.settings ?? null,
    }),

    // ─── Privacy policy (structured, admin-managed) ───────────────
    // Edited at /admin/settings/legal → "Privacy policy" tab.
    // Backend returns hero metadata + ordered sections + grievance officer
    // card; the public page renders the layout from this shape directly.
    getPrivacyPolicy: builder.query({
      query: () => '/legal/privacy',
      transformResponse: (raw) => raw || {},
    }),

    // ─── Editable page copy (admin-managed) ───────────────────────
    // One call per page returns every section's content keyed by name:
    // { sections: { hero: {...} } }. The components keep their current
    // wording as a fallback, so a slow or failing call renders exactly
    // what shipped rather than an empty page.
    getPageSections: builder.query({
      query: (page) => `/page-sections/${encodeURIComponent(page)}`,
      transformResponse: (raw) => raw?.sections ?? {},
    }),

    // ─── Terms of service (markdown, admin-managed) ───────────────
    // Unlike the privacy policy this one is a single markdown blob, not
    // ordered sections, so the Legal page splits it on its `## ` headings
    // to get the same numbered-section shape. `audience` picks which set
    // of terms: the public site always wants the user ones.
    getTermsOfService: builder.query({
      query: (audience = 'user') => `/legal/terms?audience=${audience}`,
      transformResponse: (raw) => raw || {},
    }),

    // ─── Careers — active openings (admin-managed) ─────────────────
    listCareerOpenings: builder.query({
      query: () => '/careers/openings',
      transformResponse: (raw) => raw?.openings ?? [],
    }),

    // ─── Careers — submit application (multipart) ──────────────────
    // The Apply form passes a FormData object directly so multer can
    // pick up the CV file. Don't transform — backend returns
    // { ok, id, message }.
    submitCareerApplication: builder.mutation({
      query: (formData) => ({
        url: '/careers/applications',
        method: 'POST',
        body: formData,
      }),
    }),

    // ─── Prescription upload — create a lead (multipart) ────────
    // The upload card used to flip to "received ✓" and send nothing
    // (API-NOTES 2.1). Takes a FormData so multer gets the file; the
    // browser writes the multipart boundary, so set no Content-Type.
    // Returns { request_id, files_received }.
    submitPrescription: builder.mutation({
      query: (formData) => ({
        url: '/leads/prescription',
        method: 'POST',
        body: formData,
      }),
    }),

    // ─── Callback request — create a lead (JSON) ──────────────
    // Same bug as the upload card: it used to say "Callback requested ✓"
    // and send nothing (API-NOTES 2.2). Returns { request_id }.
    submitCallback: builder.mutation({
      query: (body) => ({
        url: '/leads/callback',
        method: 'POST',
        body,
      }),
    }),

    // ─── Corporate — submit enquiry (JSON) ─────────────────────────
    // Lead form on /corporate. Goes to the corporate-inquiries inbox in
    // admin. The page falls back to a mailto draft if this errors out.
    submitCorporateEnquiry: builder.mutation({
      query: (body) => ({
        url: '/corporate/enquiry',
        method: 'POST',
        body,
      }),
    }),
  }),
});

export const {
  useListIssueCategoriesQuery,
  useGetContactInfoQuery,
  useListFaqsQuery,
  useListBlogCategoriesQuery,
  useListBlogArticlesQuery,
  useGetBlogArticleBySlugQuery,
  useGetMembershipSettingsQuery,
  useListMembershipPlansQuery,
  useListAboutTimelineQuery,
  useListAboutNumbersQuery,
  useListAboutTeamQuery,
  useListAboutAdvisoryQuery,
  useListDoctorsQuery,
  useSubmitPartnershipInquiryMutation,
  useSubmitTrustianApplicationMutation,
  useListOffersQuery,
  useListRecentTestsQuery,
  useListPopularTestsQuery,
  useListPopularPackagesQuery,
  useListCitiesQuery,
  useListCentresQuery,
  useGetAppSettingsQuery,
  useGetPrivacyPolicyQuery,
  useGetTermsOfServiceQuery,
  useGetPageSectionsQuery,
  useListCareerOpeningsQuery,
  useSubmitCareerApplicationMutation,
  useSubmitPrescriptionMutation,
  useSubmitCallbackMutation,
  useSubmitCorporateEnquiryMutation,
} = siteContentApi;







